import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DateInputPicker } from '@/components/ui/date-input-picker';
import { Edit, Save, X, Trash2, Eye } from 'lucide-react';
import type { AuditException } from '@/types/asset-audit';
import { format } from 'date-fns';

interface EditableExceptionCardProps {
  exception: AuditException;
  isEditing: boolean;
  isEditMode: boolean;
  onEdit: (id: string) => void;
  onSave: (id: string, updates: Partial<AuditException>) => void;
  onCancel: () => void;
  onDelete: (id: string) => void;
  onView: (exception: AuditException) => void;
}

export function EditableExceptionCard({
  exception,
  isEditing,
  isEditMode,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  onView,
}: EditableExceptionCardProps) {
  const [editedType, setEditedType] = useState(exception.type);
  const [editedSeverity, setEditedSeverity] = useState(exception.severity);
  const [editedStatus, setEditedStatus] = useState(exception.status);
  const [editedFollowUpDate, setEditedFollowUpDate] = useState(exception.followUpDueDate ?? '');
  const [editedNotes, setEditedNotes] = useState(exception.notes ?? '');

  const handleSave = () => {
    onSave(exception.id, {
      type: editedType,
      severity: editedSeverity,
      status: editedStatus,
      followUpDueDate: editedFollowUpDate,
      notes: editedNotes,
    });
  };

  const handleCancel = () => {
    // Reset to original values
    setEditedType(exception.type);
    setEditedSeverity(exception.severity);
    setEditedStatus(exception.status);
    setEditedFollowUpDate(exception.followUpDueDate ?? '');
    setEditedNotes(exception.notes ?? '');
    onCancel();
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical':
        return 'destructive';
      case 'High':
        return 'default';
      case 'Medium':
        return 'secondary';
      case 'Low':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Open':
        return 'destructive';
      case 'UnderReview':
        return 'default';
      case 'ActionRequired':
        return 'default';
      case 'Resolved':
        return 'secondary';
      case 'Closed':
        return 'outline';
      case 'Escalated':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/([A-Z])/g, ' $1').trim();
  };

  return (
    <Card className={isEditing ? 'border-primary' : ''}>
      <CardContent className="p-4">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-medium">{exception.assetCode}</span>
                <Badge variant={getSeverityColor(exception.severity)}>
                  {exception.severity}
                </Badge>
                <Badge variant={getStatusColor(exception.status)}>
                  {formatStatus(exception.status)}
                </Badge>
              </div>
              <div className="text-sm text-muted-foreground">
                {exception.assetName || 'Unknown Asset'}
              </div>
              {exception.location && (
                <div className="text-xs text-muted-foreground mt-1">
                  Location: {exception.location}
                </div>
              )}
            </div>
            
            {isEditMode && !isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(exception.id)}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
            )}
            
            {!isEditMode && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onView(exception)}
              >
                <Eye className="h-4 w-4 mr-1" />
                View
              </Button>
            )}
          </div>

          {/* Editable Fields */}
          {isEditing ? (
            <div className="space-y-4 pt-4 border-t">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Exception Type</Label>
                  <Select value={editedType} onValueChange={setEditedType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Missing">Missing</SelectItem>
                      <SelectItem value="LocationMismatch">Location Mismatch</SelectItem>
                      <SelectItem value="ConditionIssue">Condition Issue</SelectItem>
                      <SelectItem value="DataDiscrepancy">Data Discrepancy</SelectItem>
                      <SelectItem value="Unauthorized">Unauthorized</SelectItem>
                      <SelectItem value="Damaged">Damaged</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Severity</Label>
                  <Select value={editedSeverity} onValueChange={setEditedSeverity}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Critical">Critical</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={editedStatus} onValueChange={setEditedStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Open">Open</SelectItem>
                      <SelectItem value="UnderReview">Under Review</SelectItem>
                      <SelectItem value="ActionRequired">Action Required</SelectItem>
                      <SelectItem value="Resolved">Resolved</SelectItem>
                      <SelectItem value="Closed">Closed</SelectItem>
                      <SelectItem value="Escalated">Escalated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Follow-up Due Date</Label>
                  <DateInputPicker
                    date={editedFollowUpDate ? new Date(editedFollowUpDate) : undefined}
                    onDateChange={(date) => setEditedFollowUpDate(date ? date.toISOString().split('T')[0] : '')}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={editedNotes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditedNotes(e.target.value)}
                  placeholder="Add notes about this exception..."
                  rows={3}
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onDelete(exception.id)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete Exception
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleCancel}>
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSave}>
                    <Save className="h-4 w-4 mr-1" />
                    Save
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Exception Type</Label>
                  <Input value={exception.type} readOnly className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Follow-up Due</Label>
                  <Input
                    value={exception.followUpDueDate ? format(new Date(exception.followUpDueDate), 'MMM d, yyyy') : 'Not set'}
                    readOnly
                    className="mt-1"
                  />
                </div>
              </div>
              {exception.notes && (
                <div>
                  <Label className="text-xs text-muted-foreground">Notes</Label>
                  <Textarea
                    value={exception.notes}
                    readOnly
                    rows={2}
                    className="mt-1 resize-none"
                  />
                </div>
              )}
              <div className="text-xs text-muted-foreground pt-2">
                Reported by {exception.reportedByName} on {format(new Date(exception.reportedDate), 'MMM d, yyyy')}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
